import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import Modal from "@amzn/meridian/modal";
import { keys } from "i18n";
import { LOCATION_TYPES } from "app-constants";
import { validatePolygonForLocationType } from "utils/geoJsonUtils";

export default function PolygonEditorPanel({
  polygon,
  allPolygons,
  onUpdate,
  onDelete,
  onDeselect,
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState(polygon.locationCode);
  const [type, setType] = useState(polygon.locationType);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCode(polygon.locationCode);
    setType(polygon.locationType);
    setError(null);
  }, [polygon.id, polygon.locationCode, polygon.locationType]);

  const validate = () => {
    // Vertex count for type
    try {
      validatePolygonForLocationType(polygon.vertices, type);
    } catch (e) {
      return e.message;
    }
    // Empty code
    if (!code.trim()) {
      return t(keys.REQUIRED_ERROR_MESSAGE);
    }
    // Duplicate code
    const others = allPolygons.filter((p) => p.id !== polygon.id);
    if (others.some((p) => p.locationCode === code.trim())) {
      return `${t(keys.GEOJSON_ERROR_DUPLICATE_LOCATION_CODES)}: ${code.trim()}`;
    }
    return null;
  };

  const handleSave = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    onUpdate({ ...polygon, locationCode: code.trim(), locationType: type });
    onDeselect();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    onDelete(polygon.id);
  };

  return (
    <Column spacing="small" spacingInset="medium" width={300}>
      <Text type="h200">{t(keys.EDIT_LOCATION)}</Text>

      {error && (
        <Alert type="error" size="small">
          {error}
        </Alert>
      )}

      <Input
        label={t(keys.LOCATION_CODE)}
        value={code}
        onChange={(v) => {
          setCode(v);
          setError(null);
        }}
      />

      <Select
        label={t(keys.LOCATION_TYPE)}
        value={type}
        onChange={(v) => {
          setType(v);
          setError(null);
        }}
      >
        {LOCATION_TYPES.map((lt) => (
          <SelectOption key={lt} value={lt} label={t(lt)} />
        ))}
      </Select>

      <Button type="primary" onClick={handleSave}>
        {t(keys.SAVE)}
      </Button>
      <Button type="secondary" onClick={() => setShowDeleteConfirm(true)}>
        {t(keys.DELETE)}
      </Button>
      <Button type="secondary" onClick={onDeselect}>
        {t(keys.CANCEL)}
      </Button>

      <Modal
        width="400px"
        bodySpacingInset="medium"
        title={t(keys.DELETE)}
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <Text>{t(keys.CONFIRM_DELETE_POLYGON)}</Text>
        <Row alignmentHorizontal="right" spacingInset="large none none none">
          <Button type="secondary" minWidth="80px" onClick={() => setShowDeleteConfirm(false)}>
            {t(keys.CANCEL)}
          </Button>
          <Button type="primary" minWidth="90px" onClick={handleDelete}>
            {t(keys.YES)}
          </Button>
        </Row>
      </Modal>
    </Column>
  );
}
